import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Assignment as AssignmentIcon,
  ShieldMoon as ShieldMoonIcon,      // alt: VisibilityOffIcon
  EmojiEvents as EmojiEventsIcon,
  Domain as DomainIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  DeleteOutline as DeleteOutlineIcon,
  ContentCopy as ContentCopyIcon,
  TravelExplore as TravelExploreIcon
} from '@mui/icons-material';
import { useEffect } from 'react';
import { alpha , darken, lighten} from '@mui/material/styles';
import { BASEURL ,PRIMARYCOLOR,SECONDARYCOLOR,logoURL,COMPANY } from '../../Constants';
import { notifyCreditsChanged } from '../../services/creditsApi';

// --- Header style you requested ---
const HeaderCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: 1100,
  marginBottom: theme.spacing(4),
  padding: theme.spacing(3),
  borderRadius: 16,
  background: `linear-gradient(135deg, ${PRIMARYCOLOR} 0%, ${darken(PRIMARYCOLOR, 0.5)} 100%)`,
  color: '#fff',
  boxShadow: '0 10px 30px rgba(0,0,0,0.15)'
}));

const SectionCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: 1100,
  borderRadius: 16,
  boxShadow: '0 10px 30px rgba(0,0,0,0.08)'
}));

const StepIconContainer = styled('div')(({ theme, ownerState }) => ({
  backgroundColor: ownerState.active
    ? theme.palette.primary.main
    : ownerState.completed
    ? theme.palette.success.main
    : theme.palette.grey[300],
  color: '#fff',
  width: 36,
  height: 36,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: ownerState.active ? '0 0 0 6px rgba(25,118,210,0.15)' : 'none'
}));

function CustomStepIcon(props) {
  const { active, completed, icon } = props;
  const mapping = {
    1: <AssignmentIcon fontSize="small" />,
    2: <ShieldMoonIcon fontSize="small" />,
    3: <EmojiEventsIcon fontSize="small" />
  };
  return (
    <StepIconContainer ownerState={{ active, completed }}>
      {mapping[icon] || <AssignmentIcon fontSize="small" />}
    </StepIconContainer>
  );
}

const steps = ['Campaign Details', 'Add Domains', 'Done'];

// --- Helpers (same domain normalization as External) ---
function normalizeDomain(value) {
  if (!value) return '';
  let v = value.trim().toLowerCase();
  v = v.replace(/^https?:\/\//, ''); // strip protocol
  v = v.replace(/^www\./, '');       // strip www.
  v = v.split('/')[0];               // strip paths
  v = v.replace(/:\d+$/, '');        // strip port
  return v;
}
function isValidDomain(v) {
  const re = /^(?!\-)(?:[a-z0-9\-]{1,63}\.)+[a-z]{2,}$/i;
  return re.test(v);
}
const newId = () => (crypto?.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));

const CreateDarkWebPentest = () => {
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [pentestName, setPentestName] = useState('');
  const [organizationId, setOrganizationId] = useState(-1);
  const [organizations, setOrganizations] = useState([{ id: -1, name: 'None' }]); // TODO: fetch orgs
  const [domainInput, setDomainInput] = useState('');
  const [domainError, setDomainError] = useState('');
  const [domains, setDomains] = useState([]); // [{id, value}]
  const [isSaving, setIsSaving] = useState(false);
  const [campaignId, setCampaignId] = useState('');

  const prependNone = (list = []) => {
  const cleaned = (list || []).filter(o => o && Number(o.id) !== -1);
  return [{ id: -1, name: 'None' }, ...cleaned];
};

function loadOrganizationsBasic() {
  const token = sessionStorage.getItem('token');
  if (!token) {
    try { navigate('/'); } catch { /* optional if navigate not available */ }
    return;
  }
  fetch(`${BASEURL}/organizations?basic=1`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })
    .then(r => r.json())
    .then(data => {
      if (data?.error) {
        console.error(data.message || 'Error fetching organizations (basic)');
        setOrganizations([{ id: -1, name: 'None' }]);
        setOrganizationId(-1);
        return;
      }
      const list = Array.isArray(data?.organizations) ? data.organizations : [];
      setOrganizations(prependNone(list));
      setOrganizationId(-1); // default selection
    })
    .catch(err => {
      console.error('Failed to fetch organizations (basic):', err);
      setOrganizations([{ id: -1, name: 'None' }]);
      setOrganizationId(-1);
    });
}

// call once on mount
useEffect(() => {
  loadOrganizationsBasic();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  const canAdd = useMemo(() => domainInput.trim().length > 0, [domainInput]);

  const addDomain = () => {
    const normalized = normalizeDomain(domainInput);
    if (!normalized) return setDomainError('Enter a domain (e.g., example.com).');
    if (!isValidDomain(normalized)) return setDomainError('That doesn’t look like a valid domain.');
    if (domains.some((d) => d.value === normalized)) return setDomainError('Domain already added.');

    setDomains((prev) => [...prev, { id: newId(), value: normalized }]);
    setDomainInput('');
    setDomainError('');
  };

  const removeDomain = (id) => setDomains((prev) => prev.filter((d) => d.id !== id));
  const clearAll = () => setDomains([]);

  const copyValue = async (v) => {
    try { await navigator.clipboard.writeText(v); } catch {}
  };

  const handleSubmit = () => {
    if (!pentestName.trim() || domains.length === 0) {
      setDomainError(!pentestName.trim() ? '' : (domains.length === 0 ? 'Add at least one domain.' : ''));
      return;
    }

    setIsSaving(true);
    const token = sessionStorage.getItem('token');
    const scopeAssets = domains.map(d => String(d.value).trim()).filter(Boolean).join('\n');

    fetch(`${BASEURL}/pentests/darkweb`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        name: pentestName.trim(),
        scope: scopeAssets,
        organizationId: organizationId,
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        setIsSaving(false);
        if (data?.success === 1 && data?.pentestId) {
          notifyCreditsChanged({ reason: 'pentest-created', pentestId: data.pentestId });
          setCampaignId(data.pentestId);
          setActiveStep(2);
        } else {
          alert('Error creating pentest');
        }
      })
      .catch((err) => {
        console.error('Error creating pentest:', err);
        setIsSaving(false);
        alert('Error creating pentest');
      });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <HeaderCard>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
              Create Dark Web Monitoring Campaign
            </Typography>
            <Typography sx={{ opacity: 0.9 }}>
              Track mentions of your org across breach dumps, paste sites, forums, and marketplaces.
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <Chip
              icon={<TravelExploreIcon />}
              label="Dark Web Intel"
              variant="outlined"
              sx={{ borderColor: alpha("#fff", 0.6), color: "white", "& .MuiChip-icon": { color: "#fff" } }}
            />
            <Chip
              icon={<ShieldMoonIcon />}
              label="Continuous Monitoring"
              variant="outlined"
              sx={{ borderColor: alpha("#fff", 0.6), color: "white", "& .MuiChip-icon": { color: "#fff" } }}
            />
          </Grid>
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label, idx) => (
                <Step key={idx}>
                  <StepLabel StepIconComponent={CustomStepIcon}>
                    <Typography sx={{ color: '#fff' }}>{label}</Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Grid>
        </Grid>
      </HeaderCard>

      {/* Step 0: Campaign Details */}
      {activeStep === 0 && (
        <SectionCard sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                Campaign Details
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Give your monitoring campaign a clear, searchable name.
              </Typography>
              <TextField
                fullWidth
                label="Campaign Name"
                placeholder="e.g., Acme Dark Web Monitoring – Q3"
                value={pentestName}
                onChange={(e) => setPentestName(e.target.value)}
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth>
                <InputLabel id="org-select-label">Organization</InputLabel>
                <Select
                  labelId="org-select-label"
                  label="Organization"
                  value={organizationId}
                  onChange={(e) => setOrganizationId(Number(e.target.value))}
                >
                  {organizations.map((org) => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button variant="outlined" onClick={() => navigate(-1)}>
                  Back
                </Button>
                <Button variant="contained" onClick={() => setActiveStep(1)} disabled={!pentestName.trim()}>
                  Next
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={5} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Box
                sx={{
                  width: '100%',
                  minHeight: 220,
                  borderRadius: 3,
                  p: 3,
                  bgcolor: 'rgba(0,0,0,0.04)',
                  border: '1px dashed rgba(0,0,0,0.12)'
                }}
              >
                <Typography fontWeight={700} sx={{ mb: 1 }}>
                  Tips
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  We recommend adding your primary domain and major subsidiaries to start. You can expand later.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </SectionCard>
      )}

      {/* Step 1: Add Domains */}
      {activeStep === 1 && (
        <SectionCard sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            Add Domains to Monitor
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            We’ll normalize domains (remove http/https, paths, and ports) and prevent duplicates.
          </Typography>

          <Grid container spacing={2} alignItems="center">
            {/* Aligned input + button row (44px height to match buttons) */}
            <Grid item xs={12} md sx={{ display: 'flex', alignItems: 'center' }}>
              <TextField
                fullWidth
                size="small"
                label="Enter domain (e.g., https://example.com)"
                value={domainInput}
                onChange={(e) => {
                  setDomainInput(e.target.value);
                  setDomainError('');
                }}
                error={!!domainError}
                helperText={domainError ? domainError : null}
                FormHelperTextProps={{ sx: { m: domainError ? '3px 14px 0' : 0 } }}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DomainIcon />
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': { height: 44, alignItems: 'center' },
                  '& .MuiInputBase-input': { py: 0 }
                }}
              />
            </Grid>

            <Grid item xs={12} md="auto" sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                startIcon={<AddCircleOutlineIcon />}
                variant="contained"
                onClick={addDomain}
                disabled={!canAdd}
                sx={{ height: 44 }}
              >
                Add
              </Button>
            </Grid>
          </Grid>

          {/* Domains table */}
          <Paper elevation={0} sx={{ mt: 3, borderRadius: 3, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2.5 }}>
              <Typography fontWeight={800}>Domains ({domains.length})</Typography>
              <Tooltip title="Remove all domains">
                <span>
                  <Button color="inherit" size="small" onClick={clearAll} disabled={domains.length === 0}>
                    Clear All
                  </Button>
                </span>
              </Tooltip>
            </Box>
            <Divider />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={140}>Type</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell width={120} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {domains.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        No domains added yet. Add your first one above.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  domains.map((d) => (
                    <TableRow key={d.id} hover>
                      <TableCell>
                        <Chip size="small" icon={<DomainIcon fontSize="small" />} label="Domain" sx={{ fontWeight: 600, bgcolor: 'rgba(0,0,0,0.04)' }} />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>
                        {d.value}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Copy">
                          <IconButton size="small" onClick={() => copyValue(d.value)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => removeDomain(d.id)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>

          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button variant="outlined" onClick={() => setActiveStep(0)}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={domains.length === 0 || isSaving}
            >
              {isSaving ? <CircularProgress size={24} /> : 'Create Campaign'}
            </Button>
          </Box>
        </SectionCard>
      )}

      {/* Step 2: Done */}
      {activeStep === 2 && (
        <SectionCard sx={{ p: 4, textAlign: 'center', background: 'linear-gradient(135deg, #E3F2FD 0%, #F3E5F5 100%)' }}>
          <EmojiEventsIcon sx={{ fontSize: 56, color: 'primary.main' }} />
          <Typography variant="h4" sx={{ fontWeight: 900, mt: 1 }}>
            You’re all set! 🎉
          </Typography>
          <Typography sx={{ mt: 1, color: 'text.secondary' }}>
            Your dark web monitoring campaign has been created. You can manage it from the dashboard.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
            <Button variant="contained" onClick={() => navigate('/pentests')}>
              Finish
            </Button>
          </Box>
        </SectionCard>
      )}
    </Box>
  );
};

export default CreateDarkWebPentest;
